import dynamic from 'next/dynamic';
import { type ComponentType, type ReactElement, useMemo } from 'react';
import { Flex } from '@ironhack/design-system2/components/flex';
import { type IconProps as ChakraIconProps } from '@ironhack/design-system2/components/icon';
import {
  List,
  ListIcon,
  ListItem,
  type ListProps,
} from '@ironhack/design-system2/components/list';
import { type IconProps as FeatherIconProps } from '@ironhack/design-system2/icons';
import { Minus } from '@ironhack/design-system2/icons/minus';

import { toUpperPascalCase } from '@/lib/utils';
import { type DatoListWithIconsBlock } from '@/lib/datocms';

type Props = {
  iconProps?: ChakraIconProps;
  list: DatoListWithIconsBlock;
} & ListProps;

const importDynamicIcon = (iconName: string): ComponentType<ChakraIconProps> =>
  dynamic(
    () =>
      import('@ironhack/design-system2/icons').then((mod) => {
        const componentName = toUpperPascalCase(iconName);
        const Icon = mod[componentName] as ComponentType<FeatherIconProps>;
        const StyledListIcon = (props: ChakraIconProps) => (
          <ListIcon {...props} as={Icon} boxSize="24px" />
        );
        return StyledListIcon;
      }),
    {
      loading: () => (
        <ListIcon as={Minus} boxSize="24px" color="text.tertiary" />
      ),
      ssr: false,
    }
  );

export const ListWithIcons = (props: Props): ReactElement => {
  const {
    iconProps = {},
    list: { listItems },
    ...listProps
  } = props;

  const iconMap = useMemo<Record<string, ComponentType<ChakraIconProps>>>(
    () =>
      listItems
        .map((listItem) => listItem.icon?.[0]?.name)
        .filter(Boolean)
        .reduce<Record<string, ComponentType<ChakraIconProps>>>(
          (acc, iconName) => {
            if (!iconName || acc[iconName]) return acc;
            return {
              ...acc,
              [iconName]: importDynamicIcon(iconName),
            };
          },
          {}
        ),
    [listItems]
  );

  return (
    <List spacing={2} {...listProps}>
      {listItems.map(({ icon: [iconBlock], id, text }) => {
        const Icon = iconBlock && iconMap[iconBlock.name];
        const color = iconBlock?.color && `icon.${iconBlock.color}`;
        return (
          <ListItem alignItems="center" className={id} key={text}>
            <Flex alignItems="center" textStyle="m">
              {Icon && <Icon color={color} {...iconProps} />}
              {text}
            </Flex>
          </ListItem>
        );
      })}
    </List>
  );
};
